const MFA_TOKEN_STORAGE_KEY = 'dashboard/mfaTokensByOrg';
const LEGACY_MFA_TOKEN_STORAGE_KEY = 'dashboard/mfaToken';

type MfaTokenMap = Record<string, string>;
type MfaTokenClearListener = (orgScope: string | null) => void;

let mfaTokensByOrg: MfaTokenMap | null = null;
const clearListeners = new Set<MfaTokenClearListener>();

function getSessionStorage() {
    try {
        return typeof window !== 'undefined' ? window.sessionStorage : null;
    } catch (e) {
        return null;
    }
}

function getOrgKey(orgScope?: number | string | null) {
    if (orgScope === undefined || orgScope === null || orgScope === '') {
        return '';
    }

    return `${orgScope}`;
}

function loadTokenMap(): MfaTokenMap {
    if (mfaTokensByOrg) {
        return mfaTokensByOrg;
    }

    mfaTokensByOrg = {};
    const storage = getSessionStorage();
    const stored = storage?.getItem(MFA_TOKEN_STORAGE_KEY);
    if (stored) {
        try {
            const parsed = JSON.parse(stored);
            if (parsed && typeof parsed === 'object') {
                mfaTokensByOrg = Object.entries(parsed).reduce((acc, [key, value]) => {
                    if (typeof value === 'string' && value) {
                        acc[key] = value;
                    }
                    return acc;
                }, {} as MfaTokenMap);
            }
        } catch (e) {
            mfaTokensByOrg = {};
        }
    }

    storage?.removeItem(LEGACY_MFA_TOKEN_STORAGE_KEY);
    return mfaTokensByOrg;
}

function persistTokenMap(tokens: MfaTokenMap) {
    const storage = getSessionStorage();
    if (Object.keys(tokens).length) {
        storage?.setItem(MFA_TOKEN_STORAGE_KEY, JSON.stringify(tokens));
    } else {
        storage?.removeItem(MFA_TOKEN_STORAGE_KEY);
    }
    storage?.removeItem(LEGACY_MFA_TOKEN_STORAGE_KEY);
}

export function getMfaToken(orgScope?: number | string | null) {
    const orgKey = getOrgKey(orgScope);
    if (!orgKey) {
        return '';
    }

    return loadTokenMap()[orgKey] || '';
}

export function setMfaToken(token: string, orgScope?: number | string | null) {
    const orgKey = getOrgKey(orgScope);
    if (!orgKey) {
        return;
    }

    const tokens = { ...loadTokenMap() };
    if (token) {
        tokens[orgKey] = token;
    } else {
        delete tokens[orgKey];
    }
    mfaTokensByOrg = tokens;
    persistTokenMap(tokens);
}

function notifyTokenCleared(orgKey: string | null) {
    clearListeners.forEach((listener) => listener(orgKey));
}

export function subscribeMfaTokenCleared(listener: MfaTokenClearListener) {
    clearListeners.add(listener);
    return () => {
        clearListeners.delete(listener);
    };
}

export function clearMfaToken(orgScope?: number | string | null) {
    const orgKey = getOrgKey(orgScope);
    if (!orgKey) {
        mfaTokensByOrg = {};
        persistTokenMap({});
        notifyTokenCleared(null);
        return;
    }

    setMfaToken('', orgKey);
    notifyTokenCleared(orgKey);
}
